import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, EMPTY, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { DevWakeService } from '../services/dev-wake.service';

/** Marks a request that already went through one wake+retry so we cannot loop. */
export const DEV_WAKE_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Dev-only. When the ALB has no ECS targets it returns 503. Kick the wake
 * Lambda, toast while it boots, then send the user back to the SPA URL they
 * were on (reload). Auth/OIDC calls retry in place so login can continue.
 */
@Injectable()
export class DevWakeInterceptor implements HttpInterceptor {
  constructor(private devWake: DevWakeService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.devWake.enabled() || !this.isBackend(req.url) || req.context.get(DEV_WAKE_RETRIED)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.isSleepingStatus(error?.status)) {
          return throwError(error);
        }
        const returnTo = window.location.href;
        return from(this.devWake.ensureAwake()).pipe(
          switchMap((didWake) => {
            if (didWake && !this.isAuth(req.url)) {
              this.returnToPage(returnTo);
              return EMPTY;
            }
            return next.handle(req.clone({
              context: req.context.set(DEV_WAKE_RETRIED, true)
            }));
          })
        );
      })
    );
  }

  private returnToPage(returnTo: string): void {
    if (window.location.href === returnTo) {
      window.location.reload();
      return;
    }
    window.location.assign(returnTo);
  }

  private isSleepingStatus(status: number): boolean {
    // No healthy ALB targets → 503. If the 503 page has no CORS headers,
    // the browser reports status 0 / Failed to fetch instead (login/OIDC).
    return status === 503 || status === 502 || status === 504 || status === 0;
  }

  private isAuth(url: string): boolean {
    return url.indexOf(environment.oidcConfig.server_host) === 0;
  }

  private isBackend(url: string): boolean {
    return url.indexOf(environment.apiBaseUrl) === 0
      || url.indexOf(environment.attachmentUrl) === 0
      || url.indexOf(environment.oidcConfig.server_host) === 0;
  }
}
